
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Download, Brain, Heart, Target, BookOpen, Users, Lightbulb, Shield, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface AdvancedAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: any;
  insight: any;
}

export default function AdvancedAnalysisModal({ 
  isOpen, 
  onClose, 
  analysis, 
  insight 
}: AdvancedAnalysisModalProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Parse analysis content - check if it's a string or object
  const parseAnalysisContent = () => {
    if (typeof analysis === 'string') {
      // If analysis is a string, split by common section headers
      const sections = analysis.split(/(?=##?\s+[A-ZÁÇÕ])/g).filter(section => section.trim());
      return sections.map(section => {
        const lines = section.trim().split('\n');
        const title = lines[0].replace(/^##?\s*/, '').trim();
        const content = lines.slice(1).join('\n').trim();
        return { title, content };
      });
    } else {
      // If analysis is an object, use the existing structure
      return [
        {
          title: 'Resumo Executivo',
          content: analysis.executive_summary || analysis.summary || 'Resumo não disponível'
        },
        {
          title: 'Perfil Psicológico Profundo',
          content: analysis.psychological_profile || 'Perfil não disponível'
        },
        {
          title: 'Padrões Comportamentais',
          content: analysis.behavioral_patterns || 'Padrões não disponíveis'
        },
        {
          title: 'Mecanismos de Defesa',
          content: analysis.defense_mechanisms || 'Mecanismos não disponíveis'
        },
        {
          title: 'Vulnerabilidades Psicológicas',
          content: analysis.vulnerabilities_risks || analysis.vulnerabilities || 'Vulnerabilidades não disponíveis'
        },
        {
          title: 'Forças e Recursos Internos',
          content: analysis.strengths_resources || analysis.strengths || 'Forças não disponíveis'
        },
        {
          title: 'Recomendações Terapêuticas',
          content: analysis.therapeutic_recommendations || 'Recomendações não disponíveis'
        },
        {
          title: 'Insights Psicanalíticos',
          content: analysis.psychoanalytic_insights || 'Insights não disponíveis'
        },
        {
          title: 'Orientações para Desenvolvimento',
          content: analysis.actionable_insights || analysis.development_guidance || 'Orientações não disponíveis'
        }
      ];
    }
  };

  const sections = parseAnalysisContent();

  const getSectionIcon = (title: string) => {
    if (title.includes('Resumo') || title.includes('RESUMO')) return Brain;
    if (title.includes('Perfil') || title.includes('PERFIL')) return Heart;
    if (title.includes('Padrões') || title.includes('PADRÕES') || title.includes('Comportamental')) return Target;
    if (title.includes('Defesa') || title.includes('DEFESA') || title.includes('Mecanismos')) return Shield;
    if (title.includes('Vulnerabilidades') || title.includes('VULNERABILIDADES')) return Zap;
    if (title.includes('Forças') || title.includes('FORÇAS') || title.includes('Recursos')) return Users;
    if (title.includes('Recomendações') || title.includes('RECOMENDAÇÕES') || title.includes('Terapêuticas')) return Lightbulb;
    if (title.includes('Insights') || title.includes('INSIGHTS') || title.includes('Psicanalíticos')) return BookOpen;
    if (title.includes('Orientações') || title.includes('ORIENTAÇÕES') || title.includes('Desenvolvimento')) return Target;
    return Brain;
  };

  const getSectionColor = (title: string) => {
    if (title.includes('Resumo') || title.includes('RESUMO')) return 'purple';
    if (title.includes('Perfil') || title.includes('PERFIL')) return 'blue';
    if (title.includes('Padrões') || title.includes('PADRÕES')) return 'green';
    if (title.includes('Defesa') || title.includes('DEFESA')) return 'orange';
    if (title.includes('Vulnerabilidades') || title.includes('VULNERABILIDADES')) return 'red';
    if (title.includes('Forças') || title.includes('FORÇAS')) return 'emerald';
    if (title.includes('Recomendações') || title.includes('RECOMENDAÇÕES')) return 'indigo';
    if (title.includes('Insights') || title.includes('INSIGHTS')) return 'violet';
    if (title.includes('Orientações') || title.includes('ORIENTAÇÕES')) return 'teal';
    return 'purple';
  };

  const downloadReport = () => {
    const reportContent = `
RELATÓRIO DE ANÁLISE PSICOLÓGICA AVANÇADA
========================================

Data: ${formatDate(insight.created_at)}

${sections.map(section => `
${section.title.toUpperCase()}
${section.content}
`).join('\n')}

---
Relatório gerado por Vive Calme
    `;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analise-personalidade-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!analysis) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Brain className="h-6 w-6 text-purple-600" />
            <span>Análise Avançada - Big Five</span>
            <Badge variant="outline" className="ml-2">IA</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-muted-foreground">
            Análise personalizada baseada no modelo Big Five de personalidade
          </p>
          <Button
            onClick={downloadReport}
            variant="outline"
            size="sm"
          >
            <Download className="mr-2 h-4 w-4" />
            Baixar
          </Button>
        </div>

        <ScrollArea className="max-h-[60vh] w-full">
          <div className="space-y-6">
            <AnimatePresence>
              {sections.map((section, index) => {
                const Icon = getSectionIcon(section.title);
                const color = getSectionColor(section.title);
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center space-x-2 text-lg">
                          <Icon className={`h-5 w-5 text-${color}-600`} />
                          <span>{section.title}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-sm max-w-none">
                          {section.content.split('\n').map((paragraph, pIndex) => {
                            if (paragraph.trim().startsWith('-')) {
                              return (
                                <div key={pIndex} className="flex items-start space-x-2 mb-2">
                                  <span className={`text-${color}-600 mt-1`}>•</span>
                                  <span className="text-sm">{paragraph.substring(1).trim()}</span>
                                </div>
                              );
                            } else if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                              return (
                                <h4 key={pIndex} className={`font-semibold text-sm mt-4 mb-2 text-${color}-700`}>
                                  {paragraph.replace(/\*\*/g, '')}
                                </h4>
                              );
                            } else if (paragraph.trim()) {
                              return (
                                <p key={pIndex} className="text-sm mb-3 leading-relaxed">
                                  {paragraph}
                                </p>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </CardContent>
                    </Card>
                    {index < sections.length - 1 && <Separator className="my-4" />}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </ScrollArea>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Brain className="h-4 w-4" />
            <span>Análise ID: {insight.id?.slice(0, 8)}</span>
            <span>•</span>
            <span>Gerado em {formatDate(insight.created_at)}</span>
          </div>
          <Button onClick={onClose} variant="outline">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
